import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { HelmetProvider } from 'react-helmet-async'
import { Analytics } from '@vercel/analytics/react'
import App from './App.tsx'
import { AuthProvider } from './contexts/AuthContext.tsx'
import { CompanyProvider } from './contexts/CompanyContext.tsx'
import './index.css'

// Silence expected abort errors in dev (React StrictMode, Supabase client, React Query)
if (import.meta.env.DEV) {
  window.addEventListener('unhandledrejection', (event) => {
    const err = event.reason
    const isAbort = err instanceof DOMException && err?.name === 'AbortError'
    const msg = err?.message ?? ''
    if (isAbort || (typeof msg === 'string' && /signal is aborted|aborted without reason/i.test(msg))) {
      event.preventDefault()
      event.stopPropagation()
    }
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

try {
  const rootElement = document.getElementById('root')
  if (!rootElement) {
    throw new Error('Root element not found')
  }

  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <CompanyProvider>
              <BrowserRouter>
                <App />
                <Analytics />
              </BrowserRouter>
            </CompanyProvider>
          </AuthProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </React.StrictMode>,
  )
} catch (error) {
  console.error('Failed to render app:', error)
  const rootElement = document.getElementById('root')
  if (rootElement) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error'
    
    const container = document.createElement('div')
    container.style.cssText = 'padding: 2rem; text-align: center; font-family: system-ui, -apple-system, sans-serif;'
    
    const heading = document.createElement('h1')
    heading.style.cssText = 'font-size: 2rem; margin-bottom: 1rem; color: #1f2937;'
    heading.textContent = 'Application Error'
    
    const message = document.createElement('p')
    message.style.cssText = 'color: #6b7280; margin-bottom: 1.5rem;'
    message.textContent = errorMessage
    
    const button = document.createElement('button')
    button.style.cssText = 'padding: 0.75rem 1.5rem; background: #000; color: white; border: none; border-radius: 9999px; cursor: pointer; font-weight: 600;'
    button.textContent = 'Reload Page'
    button.onclick = () => window.location.reload()
    
    container.appendChild(heading)
    container.appendChild(message)
    container.appendChild(button)
    
    rootElement.innerHTML = ''
    rootElement.appendChild(container)
  }
}
